import * as Yup from "yup"
import React, { useEffect, useMemo } from "react"
import { useFormik } from "formik"
import Checkbox from "@mui/material/Checkbox"
import Box from "@mui/material/Box"

import MuiTextField from "../../../MuiTextField"
import MuiAutoComplete from "../../../MuiAutocomplete"
import { EFormModule } from "../.."
import { useGetChargerSkuQuery } from "../../../../pages/Common/commonService"
import { useGetProjectChargerTemplateQuery } from "../../../../pages/Projects/ProjectServices"
import { MuiTableColumn } from "../../../MuiTable"
import { TStore } from "../../../../store/Store"
import { useDispatch, useSelector } from "react-redux"
import { IDemolitionChargerForm, IDemolitionChargers, setDemolitionChargerForm } from "../../RaiseTicketFormSlice"
import { useGetChargersBySiteIdQuery } from "../../../../pages/Sites/SiteService"
import { DropdownObj } from "../../../../pages/Common/commonTypes"

import delete_red from "../../../../assets/images/delete_red.svg"

type Props = {
    module: EFormModule
    handleNextStep: () => void
}

// Validation schema for individual charger items
const chargerItemSchema = Yup.object().shape({
    serial_cin: Yup.string().notRequired(),
    charger_name: Yup.string().when("charger_id", {
        is: (charger_id: any) => !charger_id,
        then: schema => schema.required("Charger name is required"),
        otherwise: schema => schema.notRequired(),
    }),
    sku_code: Yup.object({
        id: Yup.string().test(
            'required',
            'SKU code is required',
            function (value: any) {
                const { charger_id } = this.parent
                return !Boolean(charger_id)
            }
        ),
        title: Yup.string().test(
            'required',
            'SKU code is required',
            function (value: any) {
                const { charger_id } = this.parent
                return !Boolean(charger_id)
            }
        ),
    }).when("charger_id", {
        is: (charger_id: any) => !charger_id,
        then: schema => schema.required("SKU code is required"),
        otherwise: schema => schema.notRequired(),
    }),
    charger_type: Yup.string().when("charger_id", {
        is: (charger_id: any) => !charger_id,
        then: schema => schema.required("Charger type is required"),
        otherwise: schema => schema.notRequired(),
    }),
    operational_status: Yup.string(),
})

// Validation schema for the entire form
const formSchema = Yup.object().shape({
    chargers: Yup.array().of(chargerItemSchema).min(1, "At least one charger is required"),
    selected_chargers: Yup.array().of(Yup.number()),
    is_entire_station_being_dismantled: Yup.boolean().required("Is the entire station being dismantled is required"),
})

const useRaiseTicketChargerDetails = (props: Props) => {
    const dispatch = useDispatch()

    const { demolition_charger_form, sites_form_demolition } = useSelector((state: TStore) => state.RaiseTicketForm)

    const { data: chargerSkuList } = useGetChargerSkuQuery()
    const { data: chargersBySiteId, isLoading: isChargersBySiteIdLoading } = useGetChargersBySiteIdQuery(
        { site_id: sites_form_demolition.sourceSite?.id?.toString()! },
        {
            skip: !sites_form_demolition.sourceSite?.id,
        }
    )

    const chargerSkuOptions = useMemo(() => {
        return chargerSkuList?.map(i => ({ id: i.charger_type_id, title: i.sku, type: i.name }))
    }, [chargerSkuList])

    const formik = useFormik({
        initialValues: demolition_charger_form,
        validationSchema: formSchema,
        onSubmit: values => {
            handleNext(values)
        },
    })

    // Function to update a specific charger field
    const updateChargerField = (index: number, field: keyof IDemolitionChargers, value: any) => {
        const newChargers = [...formik.values.chargers]
        newChargers[index] = { ...newChargers[index], [field]: value }
        if (field === "sku_code") {
            newChargers[index].charger_type = value?.type || ""
            newChargers[index].charger_type_id = value?.id?.toString() || ""
        }
        formik.setFieldValue("chargers", newChargers)
    }

    // Function to toggle charger selection
    const toggleChargerSelection = (chargerId: number, checked: boolean) => {
        if (checked) {
            formik.setFieldValue("selected_chargers", [...formik.values.selected_chargers, chargerId])
        } else {
            formik.setFieldValue("selected_chargers", formik.values.selected_chargers.filter((id: number) => id !== chargerId))
        }
    }

    // Function to add new charger row
    const addChargerRow = () => {
        const newCharger: IDemolitionChargers = {
            id: Date.now().toString(),
            serial_cin: "",
            charger_name: "",
            sku_code: null,
            charger_type: "",
            operational_status: "",
            oem: null,
            isSelected: false,
            isNew: true,
        }
        formik.setFieldValue("chargers", [...formik.values.chargers, newCharger])
    }

    // Function to remove charger row
    const removeChargerRow = (index: number) => {
        const newChargers = formik.values.chargers.filter((_, i) => i !== index)
        formik.setFieldValue("chargers", newChargers)
    }

    const columns: MuiTableColumn<any>[] = useMemo(
        () => [
            { id: "index", label: "S.No." },
            {
                id: "serial_cin",
                label: "CIN/Serial No.",
                render: (value: any, row: any, rowIndex: number) =>
                    row.isNew ? (
                        <MuiTextField
                            placeholder="Enter CIN/Serial No."
                            sx={{
                                width: "120px",
                            }}
                            value={formik.values.chargers[rowIndex]?.serial_cin || ""}
                            onChange={e => updateChargerField(rowIndex, "serial_cin", e.target.value)}
                            size="small"
                            fullWidth
                        />
                    ) : (
                        <Box width={"100px"} textOverflow={"ellipsis"} overflow={"hidden"} whiteSpace={"nowrap"}>
                            {row.serial_cin || "-"}
                        </Box>
                    ),
            },
            {
                id: "charger_name",
                label: "Charger Name",
                render: (value: any, row: any, rowIndex: number) =>
                    row.isNew ? (
                        <MuiTextField
                            sx={{
                                width: "120px",
                            }}
                            placeholder="Enter Charger Name"
                            value={formik.values.chargers[rowIndex]?.charger_name || ""}
                            onChange={e => updateChargerField(rowIndex, "charger_name", e.target.value)}
                            error={
                                formik.submitCount > 0 &&
                                Boolean((formik.errors.chargers as any)?.[rowIndex]?.charger_name)
                            }
                            size="small"
                            fullWidth
                        />
                    ) : (
                        <Box width={"100px"} textOverflow={"ellipsis"} overflow={"hidden"} whiteSpace={"nowrap"}>
                            {row.charger_name || "-"}
                        </Box>
                    ),
            },
            {
                id: "sku_code",
                label: "SKU Code",
                render: (value: any, row: any, rowIndex: number) => {
                    return row.isNew ? (
                        <MuiAutoComplete
                            fullWidth
                            placeholder="Select SKU Code"
                            options={chargerSkuOptions || []}
                            value={value || null}
                            sx={{
                                width: "120px",
                            }}
                            error={
                                formik.submitCount > 0 &&
                                Boolean((formik.errors.chargers as any)?.[rowIndex]?.sku_code)
                            }
                            onChange={(newValue: any) =>
                                updateChargerField(rowIndex, "sku_code", newValue || ({} as DropdownObj))
                            }
                        />
                    ) : (
                        <Box width={"100px"} textOverflow={"ellipsis"} overflow={"hidden"} whiteSpace={"nowrap"}>
                            {value.title || "-"}
                        </Box>
                    )
                },
            },
            {
                id: "charger_type",
                label: "Description",
                render: (value: string) => (
                    <Box width={"100px"} textOverflow={"ellipsis"} overflow={"hidden"} whiteSpace={"nowrap"}>
                        {value || "-"}
                    </Box>
                ),
            },
            {
                id: "status",
                label: "Operational Status",
                render: (value: string) => (
                    <Box width={"100px"} textOverflow={"ellipsis"} overflow={"hidden"} whiteSpace={"nowrap"}>
                        {value || "-"}
                    </Box>
                ),
            },
            {
                id: "sub_status",
                label: "Sub Status",
                render: (value: string) => (
                    <Box width={"100px"} textOverflow={"ellipsis"} overflow={"hidden"} whiteSpace={"nowrap"}>
                        {value || "-"}
                    </Box>
                ),
            },
            {
                id: "oem",
                label: "OEM",
                render: (value: any, row: any) =>
                    chargerSkuList?.find(sku => sku.sku === row.sku_code?.title)?.oem_name || value ||  "-"
            },
            {
                id: "custom",
                label: "",
                render: (_, row: any, rowIndex: number) =>
                    row.isNew ? (
                        <Box>
                            <img src={delete_red} alt="delete" onClick={() => removeChargerRow(rowIndex)} />
                        </Box>
                    ) : (
                        <Checkbox
                            checked={formik.values.selected_chargers.includes(row.charger_id) || false}
                            onChange={(e) => toggleChargerSelection(row.charger_id, e.target.checked)}
                        />
                    ),
            },
        ],
        [formik.values.chargers, formik.touched, formik.errors, chargerSkuOptions, formik.values.selected_chargers]
    )

    const handleNext = (values: IDemolitionChargerForm) => {
        dispatch(setDemolitionChargerForm(values))
        props.handleNextStep()
    }
    
    useEffect(() => {
        if (chargersBySiteId) {
            formik.setValues({
                chargers:
                    chargersBySiteId.map((i: any) => ({
                        ...i,
                        sku_code: i.charger_sku
                            ? {
                                  id:
                                      chargerSkuOptions?.find((option: any) => option.title === i.charger_sku)?.id ||
                                      "",
                                  title: i.charger_sku,
                              }
                            : ""
                    })) || [],
                is_entire_station_being_dismantled: false,
                selected_chargers: [],
            })
        }
    }, [chargersBySiteId])

    return {
        formik,
        columns,
        addChargerRow,
        removeChargerRow,
        updateChargerField,
        chargerData: formik.values.chargers,
        isFormValid: formik.isValid,
        formErrors: formik.errors,
        touchedFields: formik.touched,
        isChargersBySiteIdLoading,
        handleNext,
    }
}

export default useRaiseTicketChargerDetails
